import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, CreditCard, Trash2, Eye, TrendingUp, Receipt, Shield, Download } from "lucide-react";

const PAYMENT_MODES = ["cash", "upi", "card", "insurance", "other"];
const ITEM_TYPES = ["consultation", "lab_test", "radiology", "medicine", "procedure", "other"];

const defaultItem = { description: "", amount: "", type: "consultation" };

export default function Billing() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const location = useLocation();
  const [bills, setBills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPending, setFilterPending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showView, setShowView] = useState(false);
  const [viewBill, setViewBill] = useState(null);
  const [claimAction, setClaimAction] = useState(null); // { status: 'approved' | 'rejected' }
  const [claimNote, setClaimNote] = useState("");
  const [savingClaim, setSavingClaim] = useState(false);
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'approved' | 'rejected'
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const [form, setForm] = useState({ patient_id: "", items: [{ ...defaultItem }], discount: "0", payment_mode: "cash", insurance_provider_id: "", insurance_claim_amount: "0" });
  const [saving, setSaving] = useState(false);
  const [insuranceProviders, setInsuranceProviders] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/bills"), api.get("/patients"), api.get("/insurance/providers").catch(() => ({ data: [] }))]).then(([b, p, ins]) => {
      setBills(b.data); setPatients(p.data); setInsuranceProviders(ins.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Auto-enable pending filter when coming from dashboard widget via ?filter=pending
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("filter") === "pending") setFilterPending(true);
  }, [location.search]);

  const fetchBills = async () => { const { data } = await api.get("/bills"); setBills(data); };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...defaultItem }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const total = subtotal - (parseFloat(form.discount) || 0);
  const selectedProvider = insuranceProviders.find(p => p.id === form.insurance_provider_id);
  const autoClaim = selectedProvider ? (total * selectedProvider.coverage_percent / 100).toFixed(2) : "0";
  const claimAmount = parseFloat(form.insurance_claim_amount) || 0;
  const patientPayable = Math.max(total - claimAmount, 0);

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        patient_id: form.patient_id,
        items: form.items.map(i => ({ ...i, amount: parseFloat(i.amount) || 0 })),
        discount: parseFloat(form.discount) || 0,
        payment_mode: form.payment_mode,
      };
      if (form.insurance_provider_id) {
        payload.insurance_provider_id = form.insurance_provider_id;
        payload.insurance_claim_amount = claimAmount;
        payload.insurance_status = "pending";
      }
      await api.post("/bills", payload);
      toast({ title: "Bill created successfully" });
      setShowModal(false);
      setForm({ patient_id: "", items: [{ ...defaultItem }], discount: "0", payment_mode: "cash", insurance_provider_id: "", insurance_claim_amount: "0" });
      fetchBills();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const pendingClaimsCount = bills.filter(b => b.insurance_provider_id && b.insurance_status === "pending").length;
  const filtered = bills.filter(b => {
    const matchesSearch =
      b.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
      b.bill_number?.toLowerCase().includes(search.toLowerCase());
    const matchesPending = !filterPending || (b.insurance_provider_id && b.insurance_status === "pending");
    return matchesSearch && matchesPending;
  });

  const downloadClaimForm = (bill) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const items = (bill.items || []).map(i => `<tr><td>${i.description}</td><td class="r">₹${i.amount?.toFixed?.(2) || i.amount}</td></tr>`).join("");
    const audit = (bill.insurance_audit || []).map(a => `
      <tr>
        <td>${a.at?.split("T")[0] || ""}</td>
        <td>${a.from_status || "—"} → <b>${a.to_status}</b></td>
        <td>${a.by_user || ""}</td>
        <td>${a.note || "—"}</td>
      </tr>`).join("");
    const claimNumber = `CLM-${bill.bill_number || bill.id?.slice(-8)}`;
    const html = `<!DOCTYPE html><html><head><title>Claim Form ${claimNumber}</title>
<style>
  body{font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;color:#0f172a;padding:40px;max-width:820px;margin:0 auto}
  .header{display:flex;justify-content:space-between;align-items:start;border-bottom:2px solid #059669;padding-bottom:12px;margin-bottom:24px}
  .brand{font-size:22px;font-weight:800;color:#059669}
  .brand-sub{font-size:11px;color:#64748b;margin-top:2px}
  .doc-title{font-size:14px;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:.08em}
  .claim-id{font-family:ui-monospace,Menlo,monospace;font-size:11px;color:#64748b;margin-top:4px}
  .status{display:inline-block;padding:3px 10px;border-radius:10px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em}
  .s-approved{background:#d1fae5;color:#065f46}
  .s-pending{background:#fef3c7;color:#92400e}
  .s-rejected{background:#fee2e2;color:#991b1b}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:18px 28px;margin:18px 0}
  .grid h3{margin:0 0 6px;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.08em;font-weight:700}
  .field{font-size:13px;color:#0f172a;padding:5px 0;border-bottom:1px dotted #cbd5e1}
  .field span{color:#64748b;margin-right:6px;font-size:11px}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}
  th{background:#f1f5f9;text-align:left;padding:7px 10px;font-size:10px;text-transform:uppercase;color:#475569;letter-spacing:.05em}
  td{padding:7px 10px;border-bottom:1px solid #e2e8f0}
  td.r{text-align:right;font-family:ui-monospace,Menlo,monospace}
  .totals{margin-top:12px;background:#f8fafc;border-radius:8px;padding:14px}
  .totals .row{display:flex;justify-content:space-between;font-size:13px;padding:3px 0}
  .totals .total{font-weight:700;border-top:1px solid #cbd5e1;padding-top:6px;margin-top:4px}
  .totals .claim{color:#1d4ed8}
  .totals .patient-payable{color:#b45309;font-weight:700;font-size:15px;border-top:2px solid #cbd5e1;padding-top:8px;margin-top:4px}
  .section-title{font-size:12px;font-weight:700;color:#0f172a;margin:28px 0 6px;text-transform:uppercase;letter-spacing:.06em;border-left:3px solid #059669;padding-left:8px}
  .sig{margin-top:56px;display:flex;justify-content:space-between;gap:40px}
  .sig .box{flex:1;border-top:1px solid #0f172a;padding-top:6px;font-size:11px;color:#64748b}
  .footer{margin-top:28px;font-size:10px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:10px}
</style></head><body>
  <div class="header">
    <div>
      <div class="brand">MedLif Clinic</div>
      <div class="brand-sub">Insurance Claim Reimbursement Form</div>
    </div>
    <div style="text-align:right">
      <div class="doc-title">Claim Form</div>
      <div class="claim-id">${claimNumber}</div>
      <div style="margin-top:8px"><span class="status s-${bill.insurance_status || "pending"}">${bill.insurance_status || "pending"}</span></div>
    </div>
  </div>

  <div class="section-title">Patient Information</div>
  <div class="grid">
    <div class="field"><span>Name</span>${bill.patient_name || "—"}</div>
    <div class="field"><span>Patient ID</span>${bill.patient_id || "—"}</div>
    <div class="field"><span>Bill Number</span>${bill.bill_number || "—"}</div>
    <div class="field"><span>Service Date</span>${bill.created_at?.split("T")[0] || "—"}</div>
  </div>

  <div class="section-title">Insurance Provider</div>
  <div class="grid">
    <div class="field"><span>Provider</span>${bill.insurance_provider_name || "—"}</div>
    <div class="field"><span>Coverage</span>Claim Amount / Total</div>
    <div class="field"><span>Claim Status</span><span class="status s-${bill.insurance_status || "pending"}">${bill.insurance_status || "pending"}</span></div>
    <div class="field"><span>Payment Mode</span>${bill.payment_mode || "—"}</div>
  </div>

  <div class="section-title">Itemized Services</div>
  <table>
    <thead><tr><th>Description</th><th class="r">Amount</th></tr></thead>
    <tbody>${items}</tbody>
  </table>

  <div class="totals">
    <div class="row"><span>Subtotal</span><span>₹${(bill.subtotal ?? 0).toFixed ? bill.subtotal.toFixed(2) : bill.subtotal}</span></div>
    ${bill.discount ? `<div class="row"><span>Discount</span><span>− ₹${bill.discount}</span></div>` : ""}
    <div class="row total"><span>Bill Total</span><span>₹${(bill.total_amount ?? 0).toFixed ? bill.total_amount.toFixed(2) : bill.total_amount}</span></div>
    <div class="row claim"><span>Claim Amount</span><span>− ₹${(bill.insurance_claim_amount ?? 0).toFixed ? bill.insurance_claim_amount.toFixed(2) : bill.insurance_claim_amount}</span></div>
    <div class="row patient-payable"><span>Patient Payable</span><span>₹${(bill.patient_payable ?? bill.total_amount ?? 0).toFixed ? (bill.patient_payable ?? bill.total_amount).toFixed(2) : (bill.patient_payable ?? bill.total_amount)}</span></div>
  </div>

  ${audit ? `<div class="section-title">Audit Trail</div>
  <table>
    <thead><tr><th>Date</th><th>Transition</th><th>By</th><th>Note</th></tr></thead>
    <tbody>${audit}</tbody>
  </table>` : ""}

  <div class="sig">
    <div class="box">Authorized Signature (Clinic)</div>
    <div class="box">Insurance Provider Approval</div>
    <div class="box">Patient Acknowledgement</div>
  </div>

  <div class="footer">Generated ${new Date().toLocaleString()} — MedLif Clinic · Confidential</div>
  <script>window.onload=()=>{window.print();};</script>
</body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const updateClaim = (billId, newStatus) => {
    setClaimAction({ billId, status: newStatus });
    setClaimNote("");
  };

  const confirmClaim = async () => {
    if (!claimAction) return;
    setSavingClaim(true);
    try {
      const { data } = await api.put(`/bills/${claimAction.billId}/insurance-status`, {
        status: claimAction.status,
        note: claimNote,
      });
      toast({ title: `Claim ${claimAction.status}`, description: `Patient payable now ₹${data.patient_payable}` });
      setViewBill(data);
      setClaimAction(null);
      setClaimNote("");
      fetchBills();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSavingClaim(false); }
  };

  const toggleBillSelection = (id) => {
    const next = new Set(selectedBills);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedBills(next);
  };
  const selectAllVisiblePending = () => {
    const ids = filtered.filter(b => b.insurance_status === "pending" && b.insurance_provider_id).map(b => b.id);
    setSelectedBills(new Set(ids));
  };
  const clearSelection = () => setSelectedBills(new Set());

  const confirmBulk = async () => {
    if (!bulkAction || selectedBills.size === 0) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/bills/bulk-claim-update", {
        status: bulkAction,
        bill_ids: Array.from(selectedBills),
        note: bulkNote,
      });
      toast({ title: `${data.updated} claim${data.updated !== 1 ? "s" : ""} ${bulkAction}`, description: data.skipped ? `${data.skipped} skipped` : "Bulk update complete" });
      setBulkAction(null);
      setBulkNote("");
      clearSelection();
      fetchBills();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setBulkSaving(false); }
  };

  const totalRevenue = bills.reduce((s, b) => s + (b.total_amount || 0), 0);
  const todayRevenue = bills.filter(b => b.created_at?.startsWith(new Date().toISOString().slice(0,10))).reduce((s, b) => s + (b.total_amount || 0), 0);

  const modeColors = { cash: "bg-emerald-100 text-emerald-800", upi: "bg-blue-100 text-blue-800", card: "bg-violet-100 text-violet-800" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("billing")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage patient billing and payments</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={() => setShowModal(true)} data-testid="create-bill-btn">
          <Plus size={16} /> Create Bill
        </Button>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Revenue", value: `₹${totalRevenue.toLocaleString()}`, color: "text-slate-900" },
          { label: "Today's Revenue", value: `₹${todayRevenue.toLocaleString()}`, color: "text-emerald-700" },
          { label: "Total Bills", value: bills.length, color: "text-blue-700" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} className="text-slate-400" />
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{s.label}</p>
            </div>
            <p className={`text-2xl font-bold font-sans ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search bills..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="bill-search" />
          </div>
          {pendingClaimsCount > 0 && (
            <button
              onClick={() => setFilterPending(!filterPending)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                filterPending
                  ? "bg-amber-600 text-white border-amber-600 hover:bg-amber-700"
                  : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
              }`}
              data-testid="filter-pending-claims"
            >
              <Shield size={12} />
              Pending Claims ({pendingClaimsCount})
              {filterPending && <span className="ml-0.5 opacity-80">×</span>}
            </button>
          )}
          {filterPending && (
            <span className="text-xs text-slate-500">Showing {filtered.length} of {bills.length} bills</span>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {filterPending && (
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-700 font-medium">{selectedBills.size} selected</span>
            {selectedBills.size === 0 ? (
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllVisiblePending} data-testid="select-all-pending">Select all pending</Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={clearSelection} data-testid="clear-selection">Clear</Button>
            )}
          </div>
          {selectedBills.size > 0 && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5" onClick={() => setBulkAction("approved")} data-testid="bulk-approve-btn">
                <Shield size={12} /> Approve {selectedBills.size}
              </Button>
              <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5" onClick={() => setBulkAction("rejected")} data-testid="bulk-reject-btn">
                <Shield size={12} /> Reject {selectedBills.size}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {filterPending && <TableHead className="w-10"></TableHead>}
              <TableHead className="font-semibold text-slate-700">Bill #</TableHead>
              <TableHead className="font-semibold text-slate-700">Patient</TableHead>
              <TableHead className="font-semibold text-slate-700">Amount</TableHead>
              <TableHead className="font-semibold text-slate-700">Payment</TableHead>
              <TableHead className="font-semibold text-slate-700">Date</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={filterPending ? 8 : 7} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={filterPending ? 8 : 7} className="text-center py-12 text-slate-400">
                <CreditCard size={32} className="mx-auto mb-2 opacity-30" /><p>No bills found</p>
              </TableCell></TableRow>
            ) : filtered.map(bill => (
              <TableRow key={bill.id} className="table-row-hover" data-testid={`bill-row-${bill.id}`}>
                {filterPending && (
                  <TableCell>
                    {bill.insurance_status === "pending" && bill.insurance_provider_id && (
                      <input
                        type="checkbox"
                        checked={selectedBills.has(bill.id)}
                        onChange={() => toggleBillSelection(bill.id)}
                        className="w-4 h-4 rounded border-slate-300 accent-emerald-600"
                        data-testid={`bulk-check-${bill.id}`}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <span className="font-mono text-xs text-slate-600">{bill.bill_number || bill.id?.slice(-8)}</span>
                </TableCell>
                <TableCell className="font-medium text-slate-900 text-sm">{bill.patient_name}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">₹{bill.total_amount?.toLocaleString()}</p>
                    {bill.discount > 0 && <p className="text-xs text-slate-500">Disc: ₹{bill.discount}</p>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={`capitalize text-xs ${modeColors[bill.payment_mode] || "bg-slate-100 text-slate-700"}`}>{bill.payment_mode}</Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-600">{bill.created_at?.split("T")[0]}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge className={`text-xs w-fit ${bill.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{bill.status}</Badge>
                    {bill.insurance_provider_id && (
                      <Badge className={`text-[10px] w-fit gap-1 ${
                        bill.insurance_status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        bill.insurance_status === "rejected" ? "bg-rose-50 text-rose-700 border border-rose-200" :
                        "bg-amber-50 text-amber-700 border border-amber-200"
                      }`} data-testid={`row-claim-badge-${bill.id}`}>
                        <Shield size={9} /> {bill.insurance_status || "pending"}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600" onClick={() => { setViewBill(bill); setShowView(true); }} data-testid={`view-bill-${bill.id}`}>
                    <Eye size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create Bill Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-sans">Create New Bill</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Patient</Label>
                <select value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="bill-patient-select">
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.patient_id})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Payment Mode</Label>
                <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="bill-payment-select">
                  {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-slate-700">Bill Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="text-xs gap-1" data-testid="add-bill-item-btn">
                  <Plus size={12} /> Add Item
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-5 border-slate-200 text-sm"
                      placeholder="Description"
                      value={item.description}
                      onChange={e => updateItem(idx, "description", e.target.value)}
                      required
                      data-testid={`bill-item-desc-${idx}`}
                    />
                    <select
                      value={item.type}
                      onChange={e => updateItem(idx, "type", e.target.value)}
                      className="col-span-3 border border-slate-200 rounded-md px-2 py-2 text-xs bg-white"
                    >
                      {ITEM_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                    </select>
                    <Input
                      type="number"
                      className="col-span-3 border-slate-200 text-sm"
                      placeholder="₹ Amount"
                      value={item.amount}
                      onChange={e => updateItem(idx, "amount", e.target.value)}
                      required
                      data-testid={`bill-item-amount-${idx}`}
                    />
                    <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-rose-400 hover:text-rose-600 text-lg leading-none">×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Insurance */}
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                <Shield size={14} /> Insurance (optional)
              </div>
              <select value={form.insurance_provider_id} onChange={e => {
                const pid = e.target.value;
                const prov = insuranceProviders.find(p => p.id === pid);
                const auto = prov ? (total * prov.coverage_percent / 100).toFixed(2) : "0";
                setForm({ ...form, insurance_provider_id: pid, insurance_claim_amount: pid ? auto : "0" });
              }} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="bill-insurance-select">
                <option value="">No insurance</option>
                {insuranceProviders.map(p => <option key={p.id} value={p.id}>{p.name} ({p.coverage_percent}% coverage)</option>)}
              </select>
              {form.insurance_provider_id && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-blue-900">
                    Auto-calculated: <span className="font-mono font-semibold">₹{autoClaim}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-blue-900">Claim ₹</span>
                    <Input type="number" value={form.insurance_claim_amount} onChange={e => setForm({ ...form, insurance_claim_amount: e.target.value })} className="w-24 h-7 text-sm border-blue-200 text-right" data-testid="bill-insurance-claim" />
                  </div>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Discount (₹)</span>
                <Input type="number" value={form.discount} onChange={e => setForm({...form, discount: e.target.value})} className="w-24 h-7 text-sm border-slate-200 text-right" data-testid="bill-discount-input" />
              </div>
              <div className="flex justify-between text-base font-bold pt-1 border-t border-slate-200">
                <span className="text-slate-800">Bill Total</span>
                <span className="text-emerald-700">₹{total.toFixed(2)}</span>
              </div>
              {form.insurance_provider_id && claimAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-blue-700">
                    <span>Insurance Claim</span>
                    <span>− ₹{claimAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-amber-700 border-t border-slate-200 pt-1">
                    <span>Patient Payable</span>
                    <span>₹{patientPayable.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" disabled={saving} data-testid="save-bill-btn">
                <Receipt size={16} /> {saving ? "Creating..." : "Create Bill"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Bill Modal */}
      <Dialog open={showView} onOpenChange={setShowView}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">Bill Details</DialogTitle></DialogHeader>
          {viewBill && (
            <div className="space-y-4 mt-2">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{viewBill.patient_name}</p>
                  <p className="text-xs text-slate-500">{viewBill.created_at?.split("T")[0]}</p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-800">{viewBill.status}</Badge>
              </div>
              <div className="space-y-2 border-t border-slate-100 pt-3">
                {viewBill.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-700">{item.description}</span>
                    <span className="font-medium">₹{item.amount}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 pt-3 space-y-1">
                <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span>₹{viewBill.subtotal}</span></div>
                {viewBill.discount > 0 && <div className="flex justify-between text-sm text-slate-600"><span>Discount</span><span>-₹{viewBill.discount}</span></div>}
                <div className="flex justify-between text-base font-bold text-slate-900"><span>Total</span><span>₹{viewBill.total_amount}</span></div>
                <div className="flex justify-between text-sm text-slate-500"><span>Payment</span><span className="capitalize">{viewBill.payment_mode}</span></div>
              </div>

              {/* Insurance Claim section */}
              {viewBill.insurance_provider_id && (
                <div className="border-t border-slate-200 pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                      <Shield size={14} /> Insurance Claim
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${
                        viewBill.insurance_status === "approved" ? "bg-emerald-100 text-emerald-800" :
                        viewBill.insurance_status === "rejected" ? "bg-rose-100 text-rose-800" :
                        "bg-amber-100 text-amber-800"
                      }`} data-testid="bill-claim-status-badge">
                        {viewBill.insurance_status || "pending"}
                      </Badge>
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => downloadClaimForm(viewBill)} data-testid="download-claim-form-btn">
                        <Download size={12} /> Claim Form
                      </Button>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Provider</span><span className="font-medium text-slate-900">{viewBill.insurance_provider_name || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Claim Amount</span><span className="font-medium text-blue-700">₹{viewBill.insurance_claim_amount || 0}</span></div>
                    <div className="flex justify-between font-semibold"><span className="text-slate-700">Patient Payable</span><span className="text-amber-700">₹{viewBill.patient_payable ?? viewBill.total_amount}</span></div>
                  </div>

                  {/* Approve/Reject actions */}
                  {viewBill.insurance_status === "pending" && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => updateClaim(viewBill.id, "approved")} data-testid="approve-claim-btn">
                        Approve Claim
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => updateClaim(viewBill.id, "rejected")} data-testid="reject-claim-btn">
                        Reject Claim
                      </Button>
                    </div>
                  )}

                  {/* Audit log */}
                  {viewBill.insurance_audit?.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-medium">Audit Log ({viewBill.insurance_audit.length})</summary>
                      <div className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
                        {viewBill.insurance_audit.map((a, i) => (
                          <div key={i} className="bg-slate-50 rounded p-2" data-testid={`audit-entry-${i}`}>
                            <p className="text-slate-700"><span className="font-semibold">{a.from_status || "—"}</span> → <span className="font-semibold">{a.to_status}</span></p>
                            <p className="text-slate-500 text-[10px]">by {a.by_user} · {a.at?.split("T")[0]} {a.at?.split("T")[1]?.slice(0, 5)}</p>
                            {a.note && <p className="text-slate-600 italic mt-0.5">"{a.note}"</p>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Claim Approval Dialog */}
      <Dialog open={!!claimAction} onOpenChange={(v) => !v && setClaimAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              <Shield size={16} className={claimAction?.status === "approved" ? "text-emerald-600" : "text-rose-600"} />
              {claimAction?.status === "approved" ? "Approve" : "Reject"} Insurance Claim
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className={`rounded-lg p-3 text-sm ${claimAction?.status === "approved" ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
              {claimAction?.status === "approved"
                ? "Approving this claim will reduce the patient's payable amount by the claim amount."
                : "Rejecting this claim will set the patient's payable amount to the full bill total."}
            </div>
            <div>
              <Label className="text-sm text-slate-700">Note <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <textarea
                value={claimNote}
                onChange={e => setClaimNote(e.target.value)}
                placeholder={claimAction?.status === "approved" ? "e.g. Claim approved by insurer on date..." : "e.g. Coverage exceeded / Pre-existing condition..."}
                rows={3}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                data-testid="claim-note-textarea"
              />
              <p className="text-xs text-slate-500 mt-1">This note will be recorded in the audit log.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setClaimAction(null)} disabled={savingClaim}>Cancel</Button>
              <Button
                className={claimAction?.status === "approved" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                onClick={confirmClaim}
                disabled={savingClaim}
                data-testid="confirm-claim-btn"
              >
                {savingClaim ? "Saving..." : `Confirm ${claimAction?.status === "approved" ? "Approval" : "Rejection"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Claim Confirmation Dialog */}
      <Dialog open={!!bulkAction} onOpenChange={(v) => !v && setBulkAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              <Shield size={16} className={bulkAction === "approved" ? "text-emerald-600" : "text-rose-600"} />
              Bulk {bulkAction === "approved" ? "Approve" : "Reject"} Claims
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className={`rounded-lg p-3 text-sm ${bulkAction === "approved" ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
              You are about to <b>{bulkAction}</b> <b>{selectedBills.size}</b> insurance claim{selectedBills.size !== 1 ? "s" : ""}.
              {bulkAction === "approved" ? " Patient payable will be reduced by claim amounts on each bill." : " Patient payable will be set to the full bill total on each bill."}
            </div>
            <div>
              <Label className="text-sm text-slate-700">Shared Audit Note <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <textarea
                value={bulkNote}
                onChange={e => setBulkNote(e.target.value)}
                placeholder={bulkAction === "approved" ? "e.g. Batch approved per insurer EOD reconciliation" : "e.g. Batch rejected - documents missing"}
                rows={3}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                data-testid="bulk-note-textarea"
              />
              <p className="text-xs text-slate-500 mt-1">Appended to every bill's audit trail.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setBulkAction(null)} disabled={bulkSaving}>Cancel</Button>
              <Button
                className={bulkAction === "approved" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                onClick={confirmBulk}
                disabled={bulkSaving}
                data-testid="confirm-bulk-btn"
              >
                {bulkSaving ? "Processing..." : `Confirm ${bulkAction === "approved" ? "Approval" : "Rejection"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
